import { faPlus } from "@fortawesome/pro-duotone-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useQueryClient } from "@tanstack/react-query";
import { Extensions, generateJSON, JSONContent } from "@tiptap/core";
import { Editor } from "@tiptap/react";
import classNames from "classnames";
import { nanoid } from "nanoid";
import React, { useState } from "react";
import { Edge, Node } from "reactflow";
import * as Y from "yjs";

import { useQueries } from "@/hooks";
import { SemanticPaper } from "@/types";

import { addPapersToGraph } from "../semantic";
import { bulletList, listItem, semanticPaperToTipTapJSON, setNodePageContent } from "../utils";
import styles from "./SemanticResults.module.scss";

const SemanticResult = ({ paper, isSelected }: { paper: SemanticPaper; isSelected: boolean }) => {
  const [showMore, setShowMore] = useState(false);
  return (
    <div className={classNames(styles.paper, { [styles.selected]: isSelected })}>
      <div className={styles.paperHeader}>
        <h2>
          {paper.title} ({paper.year})
        </h2>
        <input type="checkbox" checked={isSelected} />
      </div>

      <div className={styles.authors}>
        Authors: {paper.authors.map((author) => author.name).join(", ")}
      </div>
      <p className={classNames(styles.abstract, { [styles.expanded]: showMore })}>
        {paper.abstract}
      </p>
      <a
        href="#"
        onClick={(e) => {
          e.stopPropagation();
          e.preventDefault();
          setShowMore(!showMore);
        }}
        className={styles.showMore}
      >
        {showMore ? "Show less" : "Show more"}
      </a>
      <div className={styles.meta}>
        {paper?.openAccessPdf?.url ? (
          <>
            <a
              href={paper?.openAccessPdf?.url}
              onClick={(e) => e.stopPropagation()}
              target="_blank"
              rel="noopener noreferrer"
            >
              PDF
            </a>
            <span className={styles.divider}>|</span>
          </>
        ) : (
          <></>
        )}
        <a
          href={paper.url}
          onClick={(e) => e.stopPropagation()}
          target="_blank"
          rel="noopener noreferrer"
        >
          URL
        </a>
        <span className={styles.divider}>|</span>
        References: {paper.referenceCount}
        <span className={styles.divider}>|</span>
        Citations: {paper.citationCount}
      </div>
    </div>
  );
};

const SemanticResults = ({
  results,
  editor,
  extensions,
  selectedNodes,
  nodesMap,
  edgesMap,
  prompt,
}: {
  results: SemanticPaper[];
  editor: Editor;
  extensions: Extensions;
  selectedNodes: Node[];
  nodesMap: Y.Map<Node> | undefined;
  edgesMap: Y.Map<Edge> | undefined;
  prompt: string;
}) => {
  const queryClient = useQueryClient();
  const { currentProject } = useQueries();
  const [selectedPapers, setSelectedPapers] = useState<string[]>([]);

  return results?.length ? (
    <div>
      {results.map((paper, index) => (
        <div
          onClick={() => {
            if (selectedPapers.includes(paper.paperId)) {
              setSelectedPapers(selectedPapers.filter((p) => p !== paper.paperId));
            } else {
              setSelectedPapers([...selectedPapers, paper.paperId]);
            }
          }}
        >
          <SemanticResult
            paper={paper}
            isSelected={selectedPapers.includes(paper.paperId)}
            key={index}
          />
        </div>
      ))}
      {Boolean(selectedPapers.length > 0) && (
        <button
          onClick={() => {
            addPapersToGraph(
              selectedNodes[0],
              results.filter((paper) => selectedPapers.includes(paper.paperId)),
              currentProject,
              nodesMap,
              edgesMap,
              editor,
              queryClient,
              prompt
            );
          }}
        >
          <FontAwesomeIcon icon={faPlus} />
          Add selected to Graph
        </button>
      )}
    </div>
  ) : (
    <></>
  );
};

export default SemanticResults;
